// A client has one client_plans link per plan year of a plan (renewal adds the
// next year's link, docs/PLAN_VERSIONING_GUIDE.md). Between a renewal and the
// new plan-year start both links are live: the current year is still in force
// and the renewal year is upcoming. Anything that needs "the" link for a plan
// picks it by date through here instead of taking whichever row comes first.

import { toDate } from "./chargeResolution";

export interface PlanYearLinkLike {
  effectiveDate?: Date | string | null;
  endDate?: Date | string | null;
}

function time(value: Date | string | null | undefined): number | null {
  if (!value) return null;
  const t = toDate(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * The link to use as of `asOf`: the plan year in force (latest start on/before
 * the date that has not ended; endDate is the inclusive last day), else the
 * nearest upcoming plan year, else the most recently ended one.
 */
export function pickPlanYearLink<T extends PlanYearLinkLike>(
  links: T[],
  asOf: Date | string = new Date(),
): T | null {
  if (links.length === 0) return null;
  const now = toDate(asOf).getTime();
  const startOf = (l: T) => time(l.effectiveDate) ?? -Infinity;

  const inForce = links
    .filter((l) => {
      const end = time(l.endDate);
      return startOf(l) <= now && (end == null || end >= now);
    })
    .sort((a, b) => startOf(b) - startOf(a));
  if (inForce.length > 0) return inForce[0];

  const upcoming = links.filter((l) => startOf(l) > now).sort((a, b) => startOf(a) - startOf(b));
  if (upcoming.length > 0) return upcoming[0];

  return [...links].sort((a, b) => startOf(b) - startOf(a))[0];
}

/** One link per plan, chosen with pickPlanYearLink. Keeps first-seen plan order. */
export function onePlanYearLinkPerPlan<T extends PlanYearLinkLike>(
  links: T[],
  planIdOf: (link: T) => number,
  asOf: Date | string = new Date(),
): T[] {
  const byPlan = new Map<number, T[]>();
  for (const link of links) {
    const planId = planIdOf(link);
    const bucket = byPlan.get(planId);
    if (bucket) bucket.push(link);
    else byPlan.set(planId, [link]);
  }
  return Array.from(byPlan.values()).map((bucket) => pickPlanYearLink(bucket, asOf)!);
}

/** True when a later plan year of the same plan already exists (i.e. this link was renewed). */
export function hasLaterPlanYear<T extends PlanYearLinkLike>(link: T, samePlanLinks: T[]): boolean {
  const start = time(link.effectiveDate);
  if (start == null) return false;
  return samePlanLinks.some((other) => other !== link && (time(other.effectiveDate) ?? -Infinity) > start);
}

function addMonths(t: number, months: number): number {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth() + months, d.getDate()).getTime();
}

/**
 * Renewing only makes sense in the run-up to the next plan year: true from
 * `monthsBefore` months ahead of the plan year's renewal date onwards (and
 * for an overdue renewal). The renewal date falls back to one year after the
 * start; a link with no dates at all can't be judged, so it stays renewable.
 *
 * `monthsAfter` is an optional upper bound for callers that want the window
 * to close once a plan has been overdue for too long (e.g. a nag/badge).
 * Left unset, the window never closes — the right default for anything that
 * gates a manual action, since a human should always be able to renew a
 * very-overdue plan.
 */
export function isRenewalWindowOpen(
  link: PlanYearLinkLike & { renewalDate?: Date | string | null },
  asOf: Date | string = new Date(),
  monthsBefore = 3,
  monthsAfter?: number,
): boolean {
  const start = time(link.effectiveDate);
  const renewal = time(link.renewalDate) ?? (start != null ? addMonths(start, 12) : null);
  if (renewal == null) return true;
  const now = toDate(asOf).getTime();
  if (now < addMonths(renewal, -monthsBefore)) return false;
  if (monthsAfter != null && now > addMonths(renewal, monthsAfter)) return false;
  return true;
}

/**
 * The plan years to offer when setting up open enrollment — one per plan,
 * minus the ones that are on their way out:
 *  - plan years that have already ended;
 *  - plan years that were renewed (their successor is what employees elect);
 *  - plan years that end before the client's next plan year begins, i.e.
 *    current plans that were not renewed into it.
 * A plan with both its current and its renewal year is shown as the renewal
 * year.
 */
export function planYearsForOpenEnrollment<
  T extends PlanYearLinkLike & { renewedToClientPlanId?: number | null },
>(links: T[], planIdOf: (link: T) => number, asOf: Date | string = new Date()): T[] {
  const now = toDate(asOf).getTime();
  const startOf = (l: T) => time(l.effectiveDate) ?? -Infinity;
  const live = links.filter((l) => {
    const end = time(l.endDate);
    return (end == null || end >= now) && l.renewedToClientPlanId == null;
  });
  const upcomingStarts = live.map(startOf).filter((s) => s > now);
  const nextPlanYearStart = upcomingStarts.length > 0 ? Math.min(...upcomingStarts) : null;
  const offered = live.filter((l) => {
    const end = time(l.endDate);
    return nextPlanYearStart == null || end == null || end >= nextPlanYearStart;
  });
  const latestPerPlan = new Map<number, T>();
  for (const link of offered) {
    const planId = planIdOf(link);
    const kept = latestPerPlan.get(planId);
    if (!kept || startOf(link) > startOf(kept)) latestPerPlan.set(planId, link);
  }
  return Array.from(latestPerPlan.values());
}
